/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Icon } from "./..";
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { itemsCenter, boxBorder, borderRadius4px, flexRow, flexWrap, displayInlineFlex, justifyCenter, parseMinWidthHeight, parseWidthHeight, pointerEventsNone, positionAbsolute, positionRelative } from "../../styles/general";
import { useColor as colors, useTheme } from "../../theme";
import { classNames } from "../../utils";
const {
  colors: {
    system: {
      rest: systemRest,
      white: systemWhite
    }
  },
  typography: {
    paragraph1,
    paragraph2,
    paragraph3
  },
  spacing
} = useTheme();
const colorMap = new Map([['default', systemRest]]);
const iconSizeMap = new Map([['medium', 24], ['large', 32], ['giant', 40]]);
const dotSizeMap = new Map([['medium', 8], ['large', 10], ['giant', 12]]);
const dotIndentMap = new Map([['medium', 4], ['large', 2]]);
const typographySizeMap = new Map([['medium', paragraph3], ['large', paragraph2], ['giant', paragraph1]]);
const calDotPos = (anchorOrigin, contentDirection, dotSize, hasContent, size) => {
  const halfDotSize = dotSize / 2;
  const {
    horizontal,
    vertical
  } = anchorOrigin;
  const dotIndent = hasContent ? dotIndentMap.get(size) || 0 : 0;
  const verticalCssRight = vertical === 'bottom' ? css`
                  bottom: ${-dotIndent}px;
              ` : css`
                  top: ${-dotIndent}px;
              `;
  const verticalCssLeft = vertical === 'bottom' ? css`
                  bottom: ${-halfDotSize}px;
              ` : css`
                  top: ${-halfDotSize}px;
              `;
  if (contentDirection === 'right') {
    if (horizontal === 'left') {
      return css`
                left: ${-dotIndent}px;
                ${verticalCssRight};
            `;
    } else {
      return css`
                left: ${`calc(100% - ${dotSize - dotIndent}px);`};
                ${verticalCssRight};
            `;
    }
  } else {
    if (horizontal === 'left') {
      return css`
                right: ${`calc(100% - ${halfDotSize}px);`};
                ${verticalCssLeft};
            `;
    } else {
      return css`
                right: ${`${-halfDotSize}px;`};
                ${verticalCssLeft};
            `;
    }
  }
};
const Badge = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  anchorOrigin,
  children,
  className,
  color,
  content,
  contentDirection,
  iconProps,
  id,
  invisible,
  max,
  name,
  showZero,
  size,
  style,
  ...props
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const typographySize = typographySizeMap.get(size);
  const iconSize = iconSizeMap.get(size);
  const dotSize = content || content === 0 && showZero ? dotSizeMap.get(size) + 8 : dotSizeMap.get(size);
  const dotColor = colors[color] || colorMap.get(color) || color;
  const dotPos = calDotPos(anchorOrigin, !children && name ? 'right' : contentDirection, dotSize, content || content === 0, size);
  const _BadgeNumberCSS = BadgeNumberCSS(typographySize, children, name, dotSize, dotColor);
  const _BadgeRootCSS = BadgeRootCSS(children, name, dotPos, _BadgeNumberCSS.name);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    let node = children;
    if (!node && name) {
      node = jsx(Icon, {
        viewBox: true,
        name: name,
        width: iconSize,
        height: iconSize,
        color: 'currentColor',
        ...iconProps
      });
    }
    return jsx("div", {
      css: _BadgeRootCSS,
      ref: ref,
      id: id,
      className: classNames('DGN-UI-Badge', className),
      style: style,
      ...props
    }, jsx("span", {
      className: 'DGN-UI-Badge-Children'
    }, node), content !== undefined && !invisible ? jsx("span", {
      css: _BadgeNumberCSS,
      className: 'DGN-UI-Badge-Dot'
    }, max && content > max ? max + '+' : content === 0 && !showZero ? null : content) : null);
  }, [anchorOrigin, children, className, color, content, contentDirection, iconProps, id, invisible, max, name, showZero, size, style, props]);
}));
const BadgeNumberCSS = (typographySize, children, name, dotSize, dotColor) => css`
    ${typographySize};
    ${flexRow};
    ${itemsCenter};
    ${justifyCenter};
    ${flexWrap};
    ${boxBorder};
    ${pointerEventsNone};
    ${children || name ? positionAbsolute : positionRelative};
    color: ${systemWhite};
    ${parseMinWidthHeight(dotSize)};
    background-color: ${dotColor};
    border-radius: ${dotSize / 2}px;
    padding: ${spacing([0, 1])};
`;
const BadgeRootCSS = (children, name, dotPos, BadgeNumberCSSName) => css`
    ${displayInlineFlex};
    ${itemsCenter};
    ${justifyCenter};
    ${positionRelative};
    ${boxBorder};
    ${borderRadius4px};
    ${parseWidthHeight('max-content')};
    ${parseMinWidthHeight('max-content')};
    .css-${BadgeNumberCSSName} {
        ${(children || name) && dotPos};
    }
`;
Badge.defaultProps = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'right'
  },
  className: '',
  color: 'default',
  content: null,
  contentDirection: 'left',
  invisible: false,
  max: 99,
  showZero: false,
  size: 'medium',
  style: {}
};
Badge.propTypes = {
  /** The anchor of the badge. */
  anchorOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOf(['left', 'right']),
    vertical: PropTypes.oneOf(['top', 'bottom'])
  }),
  /** The badge will be added relative to this node. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** The color of the component. */
  color: PropTypes.oneOfType([PropTypes.oneOf(['default', 'primary', 'info', 'success', 'warning', 'danger']), PropTypes.string]),
  /** The content rendered within the badge. */
  content: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Direction of content within the badge */
  contentDirection: PropTypes.oneOf(['left', 'right']),
  /** If `true`, the badge is invisible */
  invisible: PropTypes.bool,
  /** Max count to show. */
  max: PropTypes.number,
  /** Prop name of [ButtonIcon](https://core.diginet.com.vn/ui/?path=/docs/buttonicon) components.  */
  name: PropTypes.string,
  /** Controls whether the badge is hidden when `content` is zero. */
  showZero: PropTypes.bool,
  /** Size of the components.
   *
   * * medium (dot 8px, typography p3)
   * * large (button 10px, typography p2)
   * * giant (button 12px, typography p1)
   * */
  size: PropTypes.oneOf(['medium', 'large', 'giant']),
  /** Style inline of component. */
  style: PropTypes.object
  /**
   * ref methods (ref.current.instance.*method*)
   *
   * * option(): Gets all UI component properties
   *  * Returns value - object
   * * option(optionName): Gets the value of a single property
   *  * @param {optionName} - string
   *  * Returns value - any
   * * option(optionName, optionValue): Updates the value of a single property
   *  * @param {optionName} - string
   *  * @param {optionValue} - any
   * * option(options): Updates the values of several properties
   *  * @param {options} - object
   */
};

export { Badge };
export default OptionWrapper(Badge);